import { MapViewportEventView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { areLatLngsEqual } from '../../location/helpers';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createEventFeatureLayer } from '../helpers/feature-layers';
import { createEventGraphic } from '../helpers/graphics';
import { getEventLocation } from '../helpers/locations';
import { EsriMapEntityTypeEnum } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { adjustMapviewportEventsLocations, getEventIconType } from '../helpers/events';

type EventFeatureLayerProps = {
    mapViewportEvents: MapViewportEventView[];
    zoom: number;
    selectedEventId?: number;
    webMap?: WebMap;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const useEventFeatureLayer = ({
    mapViewportEvents,
    zoom,
    selectedEventId,
    webMap,
    selectedMapLedgerEntityType,
}: EventFeatureLayerProps) => {
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.EVENTS>(
        EntityIdToObjectIdMaps.EVENTS
    );
    const [eventsLayer, setEventsLayer] = useState<FeatureLayer>();
    const prevSelectedEventId = usePreviousLayer(selectedEventId, eventsLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        eventsLayer
    );

    const allEvents = adjustMapviewportEventsLocations(zoom, mapViewportEvents, selectedEventId);

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const eventFeatureLayer = createEventFeatureLayer();
            setEventsLayer(eventFeatureLayer);
            webMap.layers.add(eventFeatureLayer);
        }
    }, [webMap]);

    // event needs to be updated:
    // if selectedMapLedgerEntityType changed from EVENT or to EVENT
    // if event priority changed
    // if event lat/lng changed
    // if selectedEventId differs from prevSelectedEventId
    const didEventGraphicChange = useCallback(
        (currEvent: MapViewportEventView, prevEvent: MapViewportEventView) => {
            const prevEventLocation = getEventLocation(prevEvent);
            const currEventLocation = getEventLocation(currEvent);

            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.EVENT ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.EVENT);

            const isPriorityChanged = prevEvent.priority !== currEvent.priority;
            const isEventStatusChanged =
                getEventIconType(prevEvent.summaryCategory) !==
                getEventIconType(currEvent.summaryCategory);

            return (
                isEntityTypeChanged ||
                isPriorityChanged ||
                isEventStatusChanged ||
                !areLatLngsEqual(prevEventLocation, currEventLocation) ||
                (selectedEventId !== prevSelectedEventId &&
                    (currEvent.entityId === selectedEventId ||
                        currEvent.entityId === prevSelectedEventId))
            );
        },
        [
            prevSelectedEventId,
            selectedEventId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createEventGraphics = useCallback(
        (mapViewportEvents: MapViewportEventView[]) => {
            const eventGraphics: Graphic[] = [];
            for (const mapViewportEvent of mapViewportEvents) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    mapViewportEvent.entityId
                );
                const eventGraphic = createEventGraphic({
                    objectId,
                    mapViewportEvent,
                    selectedEventId,
                    selectedMapLedgerEntityType,
                });
                if (eventGraphic) {
                    eventGraphics.push(eventGraphic);
                }
            }
            return eventGraphics;
        },
        [selectedEventId, selectedMapLedgerEntityType]
    );

    useLayerControl<MapViewportEventView>({
        layer: eventsLayer,
        currItems: allEvents,
        didItemChange: didEventGraphicChange,
        createGraphics: createEventGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { eventsLayer };
};
